import React, { Component, createRef } from 'react';
import { Popup } from 'semantic-ui-react';

import '../css/SplitButton.less';

/**
 * A fully custom split button: primary action on the left, chevron dropdown on the right.
 * Uses plain HTML button elements with Semantic UI styling classes so width/height
 * are fully controllable via CSS without Button.Group layout quirks.
 *
 * Props:
 * - **label** ({string}) — primary button text
 * - **onClick** ({function}) — primary button click handler
 * - **disabled** ({boolean}) — disables primary button
 * - **popupMsg** ({string}) — tooltip shown when disabled
 * - **popupWide** ({string|boolean}) — Popup `wide` prop (e.g. 'very')
 * - **items** ({Array}) — dropdown items: { key, label, onClick, disabled, isDownloadLink, downloadUrl }
 * - **className** ({string}) — extra class applied to the container
 * - **navLabel** ({string}) — optional label rendered absolutely to the left of the button
 *  (e.g. "I would like to:"). Positioned via .action-split-button-label CSS.
 * - **onBeforeItemClick** ({function}) — optional callback fired before a dropdown item's onClick.
 *   Useful when we want a precursory click function intentionally executed (that would otherwise be canceled via stopPropagation).
 */
class SplitButton extends Component {
  constructor(props) {
    super(props);
    this.state = { isOpen: false };
    this.containerRef = createRef();
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside = (e) => {
    if (this.containerRef.current && !this.containerRef.current.contains(e.target)) {
      this.setState({ isOpen: false });
    }
  };

  toggleDropdown = (e) => {
    e.stopPropagation();
    this.setState((s) => ({ isOpen: !s.isOpen }));
  };

  handleItemClick = (item, e) => {
    e.stopPropagation();
    const { onBeforeItemClick } = this.props;
    if (typeof onBeforeItemClick === 'function') {
      onBeforeItemClick(e);
    }
    if (!item.disabled) {
      item.onClick && item.onClick();
      this.setState({ isOpen: false });
    }
  };

  render() {
    const {
      className, disabled, items = [], label, navLabel, onClick, popupMsg, popupWide
    } = this.props;
    const { isOpen } = this.state;
    const hasItems = items.length > 0;

    const primaryBtn = (
      <button
        className={`split-button-primary ui basic primary button${disabled ? ' disabled' : ''}`}
        disabled={disabled}
        onClick={disabled ? undefined : onClick}
        type='button'>
        {label}
      </button>
    );

    return (
      <div
        className={`split-button-container${className ? ` ${className}` : ''}${!hasItems ? ' split-button-solo' : ''}${isOpen ? ' is-open' : ''}`}
        ref={this.containerRef}>
        {navLabel && (
          <span className='action-split-button-label'>{navLabel}</span>
        )}
        {(disabled && popupMsg) ? (
          <Popup
            content={popupMsg}
            trigger={primaryBtn}
            wide={popupWide || false} />
        ) : primaryBtn}

        {hasItems && (
          <button
            className='split-button-chevron ui basic primary button'
            onClick={this.toggleDropdown}
            type='button'>
            <i aria-hidden='true' className='chevron down icon' />
          </button>
        )}
        {hasItems && isOpen && (
          <div className='split-button-menu'>
            {items.map((item) => (
              item.isDownloadLink ? (
                <div className='split-button-menu-item' key={item.key}>
                  <a download href={item.downloadUrl}>{item.label}</a>
                </div>
              ) : (
                <div
                  className={`split-button-menu-item${item.disabled ? ' disabled' : ''}`}
                  key={item.key}
                  onClick={(e) => this.handleItemClick(item, e)}>
                  {item.label}
                </div>
              )
            ))}
          </div>
        )}
      </div>
    );
  }
}

export default SplitButton;
